import React from "react";
import { observer } from "mobx-react";
import classNames from "classnames";
import { action, observable } from "mobx";

import Panel from "../../panel/Panel/Panel";
import OnClickOutside from "../../../../utils/OnClickOutside";
import { randomString } from "../../../../utils";
import { ChevronDown } from "../../../icons";

export enum DropdownSize {
  Small = "small",
  Medium = "medium",
  Large = "large",
}

export interface IDropdownProps {
  name?: string;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  size?: DropdownSize;
  hasLongLabel?: boolean;
  remainOpenOnSelect?: boolean;
}

const TabKey = "Tab";

@observer
export default class Dropdown extends React.Component<IDropdownProps> {
  public static defaultProps = {
    placeholder: "",
    disabled: false,
    size: DropdownSize.Small,
    remainOpenOnSelect: false,
  };

  @observable.ref
  private showingMenu: boolean = false;

  private onClickOutside: OnClickOutside;
  private identifier: string;

  constructor(props: IDropdownProps) {
    super(props);

    this.identifier = `dropdown${randomString()}`;
    const clickSelectors = [`#${this.identifier} .dropdown__button`];
    if (props.remainOpenOnSelect) {
      clickSelectors.push(`#${this.identifier} .dropdown-item`);
    }
    this.onClickOutside = new OnClickOutside(clickSelectors, this.hideDropdownFromOutside);
  }

  public render() {
    const { label, size, children } = this.props;

    return (
      <div className="dropdown" id={this.identifier}>
        {label && (
          <div className={classNames("dropdown__label", { "dropdown__label--long": this.props.hasLongLabel })}>
            {label}
          </div>
        )}
        <div
          className={classNames([
            "dropdown__button",
            "center-aligned",
            { "dropdown__button--toggled": this.showingMenu },
            `dropdown__button--${size}`,
          ])}
          onClick={this.toggleDropdown}
          onKeyUp={this.onKeyUp}
          onKeyDown={this.onKeyDown}
          tabIndex={0}
        >
          {this.renderNameOrPlaceholder()}
          <ChevronDown className="dropdown__arrow" />
        </div>
        {this.showingMenu && <Panel className="dropdown__menu fade-in-down">{children}</Panel>}
      </div>
    );
  }

  private renderNameOrPlaceholder() {
    const { name, placeholder, size } = this.props;
    const hasName = !!name;

    return (
      <span
        className={classNames([
          "dropdown__name",
          `dropdown__name--${size}`,
          { "dropdown__name--placeholder": !hasName },
        ])}
      >
        {hasName ? name : placeholder}
      </span>
    );
  }

  private onKeyUp = (event: React.KeyboardEvent) => {
    // Handles dropdown focus
    if (event.key == TabKey && !this.showingMenu) {
      this.showDropdown();
    }
  };

  private onKeyDown = (event: React.KeyboardEvent) => {
    // Handles dropdown blur
    if (event.key == TabKey) {
      this.hideDropdown();
    }
  };

  @action
  private toggleDropdown = () => {
    if (this.showingMenu) {
      this.hideDropdown();
    } else {
      this.showDropdown();
    }
  };

  @action
  private showDropdown = () => {
    this.showingMenu = true;
    this.onClickOutside.startListening();
  };

  @action
  private hideDropdown = () => {
    this.showingMenu = false;
    this.onClickOutside.stopListening();
  };

  @action
  private hideDropdownFromOutside = () => {
    this.onClickOutside.stopListening();

    // @HACK(kiahn, 2019-07-16) We need to wait till after the click event has passed
    // through React's event system before hiding the menu. Otherwise, the click event
    // will never propagate through the React dom because event target was deleted!
    setTimeout(() => {
      this.showingMenu = false;
    }, 0);
  };
}
